import React, { useState, useCallback, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  Settings as SettingsIcon,
  Save,
  Plus,
  Trash2,
  Gem,
  RefreshCw,
  Moon,
  Sun,
  Monitor,
  Check,
  AlertCircle,
  X,
} from "lucide-react";
import { PreferencesService, UserPreferences, GemDefinition } from "../services/preferencesService";
import { useTheme } from "../contexts/ThemeContext";
import { DataService } from "../services/supabase";
import { Transaction } from "../types";
import InventoryImport from "../components/InventoryImport";

interface SettingsProps {
  dataService: DataService | null;
  onTransactionsUpdate: React.Dispatch<React.SetStateAction<Transaction[]>>;
}

const Settings: React.FC<SettingsProps> = ({ dataService, onTransactionsUpdate }) => {
  const { mode, setMode, actualTheme } = useTheme();
  const [preferences, setPreferences] = useState<UserPreferences>(PreferencesService.getPreferences());
  const [showGemDialog, setShowGemDialog] = useState(false);
  const [newGem, setNewGem] = useState({ company: "", grade: "", isGem: true });
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const showSettingsMessage = useCallback((type: "success" | "error", text: string) => {
    setSettingsMessage({ type, text });
    setTimeout(() => setSettingsMessage(null), 4000);
  }, []);
  
  // Sync preferences from Supabase on mount
  useEffect(() => {
    const syncPreferences = async () => {
      try {
        const latestPrefs = await PreferencesService.forceSync();
        setPreferences(latestPrefs);
      } catch (error) {
        console.warn('Failed to sync preferences:', error);
      }
    };
    syncPreferences();
  }, []);
  
  // Group gem definitions by company
  const gemDefinitionsByCompany = preferences.gemDefinitions.reduce((acc, def) => {
    if (!acc[def.company]) {
      acc[def.company] = [];
    }
    acc[def.company].push(def);
    return acc;
  }, {} as Record<string, GemDefinition[]>);
  
  const handleSavePreferences = useCallback(() => {
    try {
      PreferencesService.savePreferences(preferences);
      setShowSaveSuccess(true);
      // Hide the checkmark after 2 seconds
      setTimeout(() => {
        setShowSaveSuccess(false);
      }, 2000);
    } catch (error) {
      showSettingsMessage("error", "Failed to save settings");
    }
  }, [preferences, showSettingsMessage]);
  
  const handleUpdateGeneralPreferences = useCallback((updates: Partial<Omit<UserPreferences, "gemDefinitions">>) => {
    setPreferences(prev => ({ ...prev, ...updates }));
  }, []);
  
  const handleAddGem = useCallback(() => {
    if (!newGem.company || !newGem.grade) {
      showSettingsMessage("error", "Please fill in both company and grade");
      return;
    }
    
    PreferencesService.addGemDefinition(newGem.company, newGem.grade, newGem.isGem);
    setPreferences(prev => ({
      ...prev,
      gemDefinitions: PreferencesService.getGemDefinitions()
    }));
    
    setNewGem({ company: "", grade: "", isGem: true });
    setShowGemDialog(false);
    showSettingsMessage("success", "Gem definition added successfully!");
  }, [newGem, showSettingsMessage]);
  
  const handleRemoveGem = useCallback((company: string, grade: string) => {
    PreferencesService.removeGemDefinition(company, grade);
    setPreferences(prev => ({
      ...prev,
      gemDefinitions: PreferencesService.getGemDefinitions()
    }));
    showSettingsMessage("success", "Gem definition removed");
  }, [showSettingsMessage]);
  
  const handleResetGemDefinitions = useCallback(() => {
    PreferencesService.resetGemDefinitions();
    setPreferences(prev => ({
      ...prev,
      gemDefinitions: PreferencesService.getGemDefinitions()
    }));
    showSettingsMessage("success", "Gem definitions reset to defaults");
  }, [showSettingsMessage]);
  
  // Handle import completion - reload transactions from the database
  const handleImportComplete = useCallback(async () => {
    if (!dataService) return;
    try {
      const transactions = await dataService.getTransactions();
      onTransactionsUpdate(transactions);
    } catch (error) {
      console.error("Failed to reload transactions after import:", error);
    }
  }, [dataService, onTransactionsUpdate]);

  return (
    <div className="min-h-screen bg-background">
      <main className="container py-8 space-y-8">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <SettingsIcon className="h-8 w-8" />
              Settings
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage your account and preferences
            </p>
          </div>
        </div>

        {/* Inline success/error message */}
        {settingsMessage && (
          <div className={`flex items-center gap-3 p-4 rounded-lg border ${
            settingsMessage.type === "success"
              ? "bg-success/10 border-success/20"
              : "bg-destructive/10 border-destructive/20"
          }`}>
            {settingsMessage.type === "success" ? (
              <div className="h-8 w-8 rounded-full bg-success/20 flex items-center justify-center flex-shrink-0">
                <Check className="h-5 w-5 text-success" />
              </div>
            ) : (
              <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0" />
            )}
            <p className={`flex-1 text-sm ${settingsMessage.type === "success" ? "text-success" : "text-destructive"}`}>
              {settingsMessage.text}
            </p>
            <Button variant="ghost" size="sm" className="flex-shrink-0" onClick={() => setSettingsMessage(null)} aria-label="Dismiss">
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
        
        {/* Import Inventory Section */}
        <InventoryImport 
          dataService={dataService} 
          onImportComplete={handleImportComplete}
        />
        
        {/* Theme Preferences */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {actualTheme === "dark" ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
              Theme Preferences
            </CardTitle>
            <CardDescription>
              Choose your preferred theme. You can follow your system settings or override them.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <RadioGroup value={mode} onValueChange={(value: string) => setMode(value as "light" | "dark" | "system")} className="gap-4">
              <div className="flex items-start space-x-3 p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors">
                <RadioGroupItem value="system" id="system" />
                <Label htmlFor="system" className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-2">
                    <Monitor className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <div className="font-medium">Follow System</div>
                      <div className="text-sm text-muted-foreground">
                        Automatically switch based on your system preference
                        {actualTheme === "dark" ? " (Currently: Dark)" : " (Currently: Light)"}
                      </div>
                    </div>
                  </div>
                </Label>
              </div>
              
              <div className="flex items-start space-x-3 p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors">
                <RadioGroupItem value="light" id="light" />
                <Label htmlFor="light" className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-2">
                    <Sun className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <div className="font-medium">Light Mode</div>
                      <div className="text-sm text-muted-foreground">
                        Always use light theme
                      </div>
                    </div>
                  </div>
                </Label>
              </div>
              
              <div className="flex items-start space-x-3 p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors">
                <RadioGroupItem value="dark" id="dark" />
                <Label htmlFor="dark" className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-2">
                    <Moon className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <div className="font-medium">Dark Mode</div>
                      <div className="text-sm text-muted-foreground">
                        Always use dark theme
                      </div>
                    </div>
                  </div>
                </Label>
              </div>
            </RadioGroup>
          </CardContent>
        </Card>
        
        {/* General Preferences */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle>General Preferences</CardTitle>
            <CardDescription>
              Configure display and calculation settings
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-6 md:grid-cols-2">
              {/* Left Column */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="date-format">Date Format</Label>
                  <Select
                    value={preferences.dateFormat}
                    onValueChange={(value) => handleUpdateGeneralPreferences({ dateFormat: value })}
                  >
                    <SelectTrigger id="date-format">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MM-DD-YYYY">MM-DD-YYYY</SelectItem>
                      <SelectItem value="DD-MM-YYYY">DD-MM-YYYY</SelectItem>
                      <SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Order of day, month, and year
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="currency-format">Currency Format</Label>
                  <Select
                    value={preferences.currencyFormat}
                    onValueChange={(value) => handleUpdateGeneralPreferences({ currencyFormat: value })}
                  >
                    <SelectTrigger id="currency-format">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD ($)</SelectItem>
                      <SelectItem value="EUR">EUR (€)</SelectItem>
                      <SelectItem value="GBP">GBP (£)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Currency for display of purchase and sale metrics
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="card-image-preview">Card Image Preview</Label>
                  <Select
                    value={preferences.cardImagePreviewBehavior}
                    onValueChange={(value: "hover" | "click") => handleUpdateGeneralPreferences({ cardImagePreviewBehavior: value })}
                  >
                    <SelectTrigger id="card-image-preview">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hover">Hover to preview</SelectItem>
                      <SelectItem value="click">Click to preview</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Controls how card photos open in inventory lists
                  </p>
                </div>
              </div>
              
              {/* Right Column */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="date-separator">Date Separator</Label>
                  <Select
                    value={preferences.dateSeparator || '/'}
                    onValueChange={(value: '/' | '-') => handleUpdateGeneralPreferences({ dateSeparator: value })}
                  >
                    <SelectTrigger id="date-separator">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="/">Slash (/)</SelectItem>
                      <SelectItem value="-">Dash (-)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Character between date components
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="rounding-decimals">Rounding Decimals</Label>
                  <Input
                    id="rounding-decimals"
                    type="number"
                    min="0"
                    max="4"
                    value={preferences.defaultCalculationSettings.roundingDecimals}
                    onChange={(e) => handleUpdateGeneralPreferences({
                      defaultCalculationSettings: {
                        ...preferences.defaultCalculationSettings,
                        roundingDecimals: parseInt(e.target.value) || 2
                      }
                    })}
                  />
                  <p className="text-xs text-muted-foreground">
                    Number of decimal places for calculations
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="card-image-preview-size">Preview Bubble Size</Label>
                  <Select
                    value={preferences.cardImagePreviewSize}
                    onValueChange={(value: "small" | "medium" | "large") => handleUpdateGeneralPreferences({ cardImagePreviewSize: value })}
                  >
                    <SelectTrigger id="card-image-preview-size">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="small">Small</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="large">Large</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Sets the size of the hover preview bubble
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Gem Definitions */}
        <Card className="glass-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Gem className="h-5 w-5" />
                  Gem Definitions
                </CardTitle>
                <CardDescription>
                  Configure which grades qualify as 'gems' for each grading company
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowGemDialog(true)}
                  className="gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Rule
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleResetGemDefinitions}
                  className="gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Reset
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <Badge variant="outline">
                {preferences.gemDefinitions.length} rules configured
              </Badge>
            </div>
            
            <Accordion type="multiple" className="w-full">
              {Object.entries(gemDefinitionsByCompany).map(([company, definitions]) => (
                <AccordionItem key={company} value={company}>
                  <AccordionTrigger>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{company}</span>
                      <Badge variant="success" className="bg-success/20 border-success">
                        {definitions.filter(d => d.isGem).length} gems
                      </Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2 pt-2">
                      {definitions
                        .sort((a, b) => parseFloat(b.grade) - parseFloat(a.grade))
                        .map((def) => (
                        <div
                          key={def.id}
                          className="flex items-center justify-between p-2 hover:bg-muted rounded-md"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-sm">Grade {def.grade}</span>
                            <Badge
                              variant={def.isGem ? "success" : "outline"}
                              className={def.isGem ? "bg-success/20 border-success" : ""}
                            >
                              {def.isGem ? "Gem" : "Not Gem"}
                            </Badge>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveGem(def.company, def.grade)}
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>
        
        {/* Save Button */}
        <div className="flex justify-end">
          <Button variant="gradient" size="lg" onClick={handleSavePreferences} className="gap-2 relative">
            {showSaveSuccess ? (
              <>
                <Check className="h-4 w-4 animate-in fade-in zoom-in duration-200" />
                <span className="animate-in fade-in slide-in-from-left-2 duration-200">Saved!</span>
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Save Settings
              </>
            )}
          </Button>
        </div>
      </main>
      
      {/* Add Gem Dialog */}
      <Dialog open={showGemDialog} onOpenChange={setShowGemDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Custom Gem Definition</DialogTitle>
            <DialogDescription>
              Define which grades qualify as gems for a grading company
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="gem-company">Company</Label>
              <Input
                id="gem-company"
                value={newGem.company}
                onChange={(e) => setNewGem(prev => ({ ...prev, company: e.target.value }))}
                placeholder="e.g., PSA, BGS, SGC"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="gem-grade">Grade</Label>
              <Input
                id="gem-grade"
                value={newGem.grade}
                onChange={(e) => setNewGem(prev => ({ ...prev, grade: e.target.value }))}
                placeholder="e.g., 10, 9.5, 9"
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="gem-checkbox"
                checked={newGem.isGem}
                onChange={(e) => setNewGem(prev => ({ ...prev, isGem: e.target.checked }))}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="gem-checkbox" className="text-sm font-normal cursor-pointer">
                This grade qualifies as a gem
              </Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGemDialog(false)}>
              Cancel
            </Button>
            <Button variant="gradient" onClick={handleAddGem}>
              <Plus className="h-4 w-4 mr-2" />
              Add Rule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Settings;
